// src/pages/AnalyticsPage.tsx
// Detailed analytics: Sales Detail (item comparison) + AR Detail (customer aging with flags)
// Two tabs, date range pickers with Hebrew toggle, multi-filter system

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft, Search, RefreshCw, ChevronDown,
  DollarSign, Hash, Eye, BarChart3,
  X, CreditCard,
} from 'lucide-react';
import { useAuthContext } from '../hooks/useAuth';
import { api } from '../services/api';
import type { ARCustomerDetail, AnalyticsFilters } from '../types';
import HebrewRangePicker from '../components/HebrewDatePicker';

// ══════════════════════════════════════════════════════════════
// Constants & Helpers
// ══════════════════════════════════════════════════════════════

const currentYear = new Date().getFullYear();
const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };
const yearStartStr = (y = currentYear) => `${y}-01-01`;
const fmtCur = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
const fmtNum = (v: number) => new Intl.NumberFormat('en-US').format(Math.round(v));
const fmtDate = (d: string | null) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return d; } };

// Hebrew month names for the date picker

interface AnalyticsPageProps { onBack?: () => void; }

// ══════════════════════════════════════════════════════════════
// Date Range Picker with Hebrew Toggle
// ══════════════════════════════════════════════════════════════

function DateRangePicker({ label, startDate, endDate, onStartChange, onEndChange }: {
  label: string; startDate: string; endDate: string;
  onStartChange: (v: string) => void; onEndChange: (v: string) => void;
}) {
  const [showHebrew, setShowHebrew] = useState(false);
 
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">{label}</span>
        <button onClick={() => setShowHebrew(!showHebrew)}
          className={`text-[10px] px-1.5 py-0.5 rounded border transition-all ${showHebrew ? 'border-blue-500 text-blue-400 bg-blue-500/10' : 'border-slate-600 text-slate-500 hover:text-slate-300'}`}
        >{showHebrew ? 'Hebrew Calendar' : 'HEB'}</button>
      </div>
 
      {!showHebrew ? (
        <div className="flex items-center gap-1.5">
          <input type="date" value={startDate} onChange={e => onStartChange(e.target.value)}
            className="px-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50 w-[130px]" />
          <span className="text-slate-600 text-xs">→</span>
          <input type="date" value={endDate} onChange={e => onEndChange(e.target.value)}
            className="px-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50 w-[130px]" />
        </div>
      ) : (
        <HebrewRangePicker
          onApply={(startGreg, endGreg) => {
            onStartChange(startGreg);
            onEndChange(endGreg);
          }}
        />
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// Filter Select
// ══════════════════════════════════════════════════════════════

function FilterSelect({ label, value, onChange, options, placeholder = 'All' }: {
  label: string; value: string; onChange: (v: string) => void;
  options: { value: string; label: string }[]; placeholder?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">{label}</span>
      <div className="relative">
        <select value={value} onChange={e => onChange(e.target.value)}
          className="appearance-none px-2.5 py-1.5 pr-7 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50 cursor-pointer min-w-[120px]">
          <option value="">{placeholder}</option>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// Display Toggle (Qty / Dollars / Both)
// ══════════════════════════════════════════════════════════════

type DisplayMode = 'both' | 'qty' | 'dollars';

function DisplayToggle({ mode, onChange }: { mode: DisplayMode; onChange: (m: DisplayMode) => void }) {
  const opts: { value: DisplayMode; label: string; icon: typeof Hash }[] = [
    { value: 'both', label: 'Both', icon: Eye },
    { value: 'qty', label: 'Qty', icon: Hash },
    { value: 'dollars', label: '$', icon: DollarSign },
  ];
  return (
    <div className="flex rounded-md border border-slate-700/50 overflow-hidden">
      {opts.map(o => (
        <button key={o.value} onClick={() => onChange(o.value)}
          className={`px-2.5 py-1.5 text-xs flex items-center gap-1 transition-all ${mode === o.value ? 'bg-blue-600 text-white' : 'bg-slate-800/50 text-slate-400 hover:text-white'}`}>
          <o.icon size={11} />{o.label}
        </button>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// Change Badge
// ══════════════════════════════════════════════════════════════

function ChangeBadge({ value }: { value: number | null }) {
  if (value === null) return <span className="text-slate-600 text-xs">NEW</span>;
  if (value === -100) return <span className="text-red-400 text-xs">DROPPED</span>;
  const color = value > 0 ? 'text-emerald-400' : value < 0 ? 'text-red-400' : 'text-slate-400';
  return <span className={`text-xs font-medium ${color}`}>{value > 0 ? '+' : ''}{value.toFixed(1)}%</span>;
}

// ══════════════════════════════════════════════════════════════
// Flag Badges for AR
// ══════════════════════════════════════════════════════════════

function FlagBadges({ row }: { row: ARCustomerDetail }) {
  const badges = [];
  if (row.hasSkipped) badges.push({ label: 'Skipped', color: 'bg-red-500/20 text-red-300 border-red-500/30' });
  if (row.shortpaidCount > 0) badges.push({ label: `Short (${row.shortpaidCount})`, color: 'bg-amber-500/20 text-amber-300 border-amber-500/30' });
  if (row.overpaidCount > 0) badges.push({ label: `Over (${row.overpaidCount})`, color: 'bg-blue-500/20 text-blue-300 border-blue-500/30' });
  if (row.hasPostDated) badges.push({ label: 'Post-dated', color: 'bg-purple-500/20 text-purple-300 border-purple-500/30' });
  if (row.noRecentPurchases) badges.push({ label: 'No recent', color: 'bg-slate-500/20 text-slate-300 border-slate-500/30' });
  if (!badges.length) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {badges.map(b => <span key={b.label} className={`text-[10px] px-1.5 py-0.5 rounded border ${b.color}`}>{b.label}</span>)}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// Sales Detail Tab
// ══════════════════════════════════════════════════════════════

function SalesDetailTab({ filters }: { filters?: AnalyticsFilters }) {
  const [p1Start, setP1Start] = useState(yearStartStr());
  const [p1End, setP1End] = useState(todayStr());
  const [p2Start, setP2Start] = useState(yearStartStr(currentYear - 1));
  const [p2End, setP2End] = useState(() => { const d = todayStr().split('-'); d[0] = String(currentYear - 1); return d.join('-'); });
  const [salesman, setSalesman] = useState('');
  const [customer, setCustomer] = useState('');
  const [driver, setDriver] = useState('');
  const [category, setCategory] = useState('');
  const [displayMode, setDisplayMode] = useState<DisplayMode>('both');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<'p1Dollars' | 'p1Qty' | 'dollarsChange' | 'qtyChange'>('p1Dollars');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['salesDetail', p1Start, p1End, p2Start, p2End, salesman, customer, driver, category],
    queryFn: () => api.getSalesDetail({ p1Start, p1End, p2Start, p2End, salesman: salesman || undefined, customer: customer || undefined, driver: driver || undefined, category: category || undefined }),
    retry: 1,
  });

  const filteredItems = useMemo(() => {
    if (!data?.items) return [];
    let items = data.items;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      items = items.filter(i => i.itemNo.toLowerCase().includes(s) || i.description.toLowerCase().includes(s));
    }
    items.sort((a, b) => {
      const aVal = a[sortField] ?? 0;
      const bVal = b[sortField] ?? 0;
      return sortDir === 'desc' ? (bVal as number) - (aVal as number) : (aVal as number) - (bVal as number);
    });
    return items;
  }, [data, searchTerm, sortField, sortDir]);

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const showQty = displayMode === 'qty' || displayMode === 'both';
  const showDol = displayMode === 'dollars' || displayMode === 'both';

  return (
    <div className="space-y-4">
      {/* Filters row */}
      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        <DateRangePicker label={`Period 1 (${currentYear})`} startDate={p1Start} endDate={p1End} onStartChange={setP1Start} onEndChange={setP1End} />
        <DateRangePicker label={`Period 2 (${currentYear-1})`} startDate={p2Start} endDate={p2End} onStartChange={setP2Start} onEndChange={setP2End} />
        <div className="h-px w-px" /> {/* spacer */}
        {filters && <>
          <FilterSelect label="Salesman" value={salesman} onChange={setSalesman} options={filters.salesmen.map(s => ({ value: s, label: s }))} />
          <FilterSelect label="Category" value={category} onChange={setCategory} options={filters.categories.map(c => ({ value: c, label: c }))} />
          <FilterSelect label="Route" value={driver} onChange={setDriver} options={filters.drivers.map(d => ({ value: d, label: d }))} />
          <FilterSelect label="Customer" value={customer} onChange={setCustomer}
            options={filters.customers.map(c => ({ value: String(c.code), label: `${c.code} - ${c.name}` }))} placeholder="All Customers" />
        </>}
        <div className="flex-1" />
        <DisplayToggle mode={displayMode} onChange={setDisplayMode} />
        <button onClick={() => refetch()} disabled={isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Summary cards */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {showDol && <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
            <p className="text-xs text-slate-500">P1 Sales</p><p className="text-lg font-bold text-white">{fmtCur(data.p1Total)}</p>
          </div>}
          {showDol && <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
            <p className="text-xs text-slate-500">P2 Sales</p><p className="text-lg font-bold text-white">{fmtCur(data.p2Total)}</p>
          </div>}
          {showQty && <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
            <p className="text-xs text-slate-500">P1 Qty</p><p className="text-lg font-bold text-white">{fmtNum(data.p1QtyTotal)}</p>
          </div>}
          {showQty && <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
            <p className="text-xs text-slate-500">P2 Qty</p><p className="text-lg font-bold text-white">{fmtNum(data.p2QtyTotal)}</p>
          </div>}
        </div>
      )}

      {/* Search */}
      <div className="relative w-64">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
        <input type="text" placeholder="Search items..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          className="w-full pl-8 pr-3 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500/50" />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center h-40"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : (
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-700/50 bg-slate-800/50">
                  <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Item</th>
                  <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Description</th>
                  {showQty && <th className="px-3 py-2.5 text-right text-slate-400 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('p1Qty')}>P1 Qty {sortField === 'p1Qty' && (sortDir === 'desc' ? '↓' : '↑')}</th>}
                  {showQty && <th className="px-3 py-2.5 text-right text-slate-400 font-medium">P2 Qty</th>}
                  {showQty && <th className="px-3 py-2.5 text-right text-slate-400 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('qtyChange')}>Qty Δ {sortField === 'qtyChange' && (sortDir === 'desc' ? '↓' : '↑')}</th>}
                  {showDol && <th className="px-3 py-2.5 text-right text-slate-400 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('p1Dollars')}>P1 $ {sortField === 'p1Dollars' && (sortDir === 'desc' ? '↓' : '↑')}</th>}
                  {showDol && <th className="px-3 py-2.5 text-right text-slate-400 font-medium">P2 $</th>}
                  {showDol && <th className="px-3 py-2.5 text-right text-slate-400 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('dollarsChange')}>$ Δ {sortField === 'dollarsChange' && (sortDir === 'desc' ? '↓' : '↑')}</th>}
                  {showDol && <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Avg P1</th>}
                  {showDol && <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Avg P2</th>}
                </tr>
              </thead>
              <tbody>
                {filteredItems.map(item => (
                  <tr key={item.itemNo} className="border-b border-slate-800/50 hover:bg-slate-700/20 transition-colors">
                    <td className="px-3 py-2 font-mono text-slate-300">{item.itemNo}</td>
                    <td className="px-3 py-2 text-white max-w-[200px] truncate">{item.description}</td>
                    {showQty && <td className="px-3 py-2 text-right text-slate-300">{fmtNum(item.p1Qty)}</td>}
                    {showQty && <td className="px-3 py-2 text-right text-slate-500">{fmtNum(item.p2Qty)}</td>}
                    {showQty && <td className="px-3 py-2 text-right"><ChangeBadge value={item.qtyChange} /></td>}
                    {showDol && <td className="px-3 py-2 text-right text-slate-300">{fmtCur(item.p1Dollars)}</td>}
                    {showDol && <td className="px-3 py-2 text-right text-slate-500">{fmtCur(item.p2Dollars)}</td>}
                    {showDol && <td className="px-3 py-2 text-right"><ChangeBadge value={item.dollarsChange} /></td>}
                    {showDol && <td className="px-3 py-2 text-right text-slate-400">{item.avgPriceP1 > 0 ? `$${item.avgPriceP1.toFixed(2)}` : '—'}</td>}
                    {showDol && <td className="px-3 py-2 text-right text-slate-500">{item.avgPriceP2 > 0 ? `$${item.avgPriceP2.toFixed(2)}` : '—'}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredItems.length === 0 && !isLoading && (
            <div className="text-center py-8 text-slate-500 text-sm">No items found for the selected period and filters</div>
          )}
          <div className="px-3 py-2 border-t border-slate-700/30 text-xs text-slate-500">{filteredItems.length} items</div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// AR Detail Tab
// ══════════════════════════════════════════════════════════════

function ARDetailTab({ filters }: { filters?: AnalyticsFilters }) {
  const [salesman, setSalesman] = useState('');
  const [category, setCategory] = useState('');
  const [flag, setFlag] = useState('');
  const [aging, setAging] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<number | null>(null);
  const [sortField, setSortField] = useState<'balance' | 'avgAging' | 'over90' | 'name'>('balance');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['arDetail', salesman, category, flag, aging],
    queryFn: () => api.getARDetail({ salesman: salesman || undefined, category: category || undefined, flag: flag || undefined, aging: aging || undefined }),
    retry: 1,
  });

  const { data: drilldown } = useQuery({
    queryKey: ['arDrilldown', selectedCustomer],
    queryFn: () => api.getARDrilldown(selectedCustomer!),
    enabled: selectedCustomer !== null,
    retry: 1,
  });

  const filteredCustomers = useMemo(() => {
    if (!data?.customers) return [];
    let custs = data.customers;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      custs = custs.filter(c => c.name.toLowerCase().includes(s) || String(c.code).includes(s));
    }
    custs.sort((a, b) => {
      if (sortField === 'name') return sortDir === 'desc' ? b.name.localeCompare(a.name) : a.name.localeCompare(b.name);
      return sortDir === 'desc' ? (b[sortField] as number) - (a[sortField] as number) : (a[sortField] as number) - (b[sortField] as number);
    });
    return custs;
  }, [data, searchTerm, sortField, sortDir]);

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const summary = data?.summary;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        {filters && <>
          <FilterSelect label="Salesman" value={salesman} onChange={setSalesman} options={filters.salesmen.map(s => ({ value: s, label: s }))} />
          <FilterSelect label="Category" value={category} onChange={setCategory} options={filters.categories.map(c => ({ value: c, label: c }))} />
        </>}
        <FilterSelect label="Flag" value={flag} onChange={setFlag} options={[
          { value: 'skipped', label: 'Skipped Invoices' },
          { value: 'shortpaid', label: 'Short Paid' },
          { value: 'overpaid', label: 'Over Paid' },
          { value: 'postdated', label: 'Post-dated Payments' },
          { value: 'norecent', label: 'No Recent Purchases' },
        ]} placeholder="All Flags" />
        <FilterSelect label="Aging" value={aging} onChange={setAging} options={[
          { value: 'current', label: 'Current (0-30)' },
          { value: '31-60', label: '31-60 Days' },
          { value: '61-90', label: '61-90 Days' },
          { value: 'over90', label: 'Over 90 Days' },
        ]} placeholder="All Aging" />
        <div className="flex-1" />
        <button onClick={() => refetch()} disabled={isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
            <p className="text-xs text-slate-500">Total AR</p><p className="text-lg font-bold text-white">{fmtCur(summary.totalAR)}</p>
          </div>
          <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
            <p className="text-xs text-emerald-400/70">Current</p><p className="text-lg font-bold text-emerald-300">{fmtCur(summary.totalCurrent)}</p>
          </div>
          <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
            <p className="text-xs text-blue-400/70">31-60</p><p className="text-lg font-bold text-blue-300">{fmtCur(summary.totalOver30)}</p>
          </div>
          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <p className="text-xs text-amber-400/70">61-90</p><p className="text-lg font-bold text-amber-300">{fmtCur(summary.totalOver60)}</p>
          </div>
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
            <p className="text-xs text-red-400/70">Over 90</p><p className="text-lg font-bold text-red-300">{fmtCur(summary.totalOver90)}</p>
          </div>
          <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
            <p className="text-xs text-slate-500">Flags</p>
            <div className="flex gap-2 mt-1 text-[10px]">
              {summary.skippedCount > 0 && <span className="text-red-300">{summary.skippedCount} skip</span>}
              {summary.shortpaidCount > 0 && <span className="text-amber-300">{summary.shortpaidCount} short</span>}
              {summary.overpaidCount > 0 && <span className="text-blue-300">{summary.overpaidCount} over</span>}
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative w-64">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
        <input type="text" placeholder="Search customers..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          className="w-full pl-8 pr-3 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500/50" />
      </div>

      {/* Drilldown Panel */}
      {selectedCustomer !== null && drilldown && (
        <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-white">{drilldown.customer.name} (#{drilldown.customer.code})</h3>
            <button onClick={() => setSelectedCustomer(null)} className="text-slate-400 hover:text-white"><X size={16} /></button>
          </div>
          <div className="flex flex-wrap gap-4 text-xs text-slate-400">
            <span>Balance: <span className="text-white font-medium">{fmtCur(drilldown.customer.balance)}</span></span>
            <span>Credit Limit: {fmtCur(drilldown.customer.creditLimit)}</span>
            <span>Avg Aging: {drilldown.customer.avgAging} days</span>
            <span>Last Payment: {fmtDate(drilldown.customer.lastPaymentDate)} ({fmtCur(drilldown.customer.lastPaymentAmount)})</span>
          </div>
          {/* Invoice list */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b border-slate-700/30">
                <th className="px-2 py-1.5 text-left text-slate-400">Invoice</th>
                <th className="px-2 py-1.5 text-left text-slate-400">Date</th>
                <th className="px-2 py-1.5 text-right text-slate-400">Total</th>
                <th className="px-2 py-1.5 text-right text-slate-400">Paid</th>
                <th className="px-2 py-1.5 text-right text-slate-400">Balance</th>
                <th className="px-2 py-1.5 text-right text-slate-400">Days</th>
                <th className="px-2 py-1.5 text-left text-slate-400">Flags</th>
              </tr></thead>
              <tbody>
                {drilldown.invoices.map(inv => (
                  <tr key={inv.invoice} className="border-b border-slate-800/30 hover:bg-slate-700/10">
                    <td className="px-2 py-1.5 font-mono text-slate-300">{inv.invoice}</td>
                    <td className="px-2 py-1.5 text-slate-400">{fmtDate(inv.date)}</td>
                    <td className="px-2 py-1.5 text-right text-slate-300">{fmtCur(inv.total)}</td>
                    <td className="px-2 py-1.5 text-right text-emerald-400">{fmtCur(inv.paid)}</td>
                    <td className={`px-2 py-1.5 text-right font-medium ${inv.balance < 0 ? 'text-blue-400' : 'text-amber-400'}`}>{fmtCur(inv.balance)}</td>
                    <td className="px-2 py-1.5 text-right text-slate-400">{inv.daysOld}</td>
                    <td className="px-2 py-1.5">
                      <div className="flex gap-1">
                        {inv.flags.map(f => <span key={f} className={`text-[9px] px-1 py-0.5 rounded border ${f === 'skipped' ? 'border-red-500/30 text-red-300' : f === 'shortpaid' ? 'border-amber-500/30 text-amber-300' : 'border-blue-500/30 text-blue-300'}`}>{f}</span>)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Customer Table */}
      {isLoading ? (
        <div className="flex items-center justify-center h-40"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : (
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-700/50 bg-slate-800/50">
                  <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Code</th>
                  <th className="px-3 py-2.5 text-left text-slate-400 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('name')}>Customer {sortField === 'name' && (sortDir === 'desc' ? '↓' : '↑')}</th>
                  <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Salesman</th>
                  <th className="px-3 py-2.5 text-right text-slate-400 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('balance')}>Balance {sortField === 'balance' && (sortDir === 'desc' ? '↓' : '↑')}</th>
                  <th className="px-3 py-2.5 text-right text-emerald-400/70 font-medium">Current</th>
                  <th className="px-3 py-2.5 text-right text-blue-400/70 font-medium">31-60</th>
                  <th className="px-3 py-2.5 text-right text-amber-400/70 font-medium">61-90</th>
                  <th className="px-3 py-2.5 text-right text-red-400/70 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('over90')}>90+ {sortField === 'over90' && (sortDir === 'desc' ? '↓' : '↑')}</th>
                  <th className="px-3 py-2.5 text-right text-slate-400 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('avgAging')}>Avg Days {sortField === 'avgAging' && (sortDir === 'desc' ? '↓' : '↑')}</th>
                  <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Flags</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map(row => (
                  <tr key={row.code}
                    onClick={() => setSelectedCustomer(selectedCustomer === row.code ? null : row.code)}
                    className={`border-b border-slate-800/50 cursor-pointer transition-colors ${selectedCustomer === row.code ? 'bg-blue-500/10' : 'hover:bg-slate-700/20'}`}>
                    <td className="px-3 py-2 font-mono text-slate-500">{row.code}</td>
                    <td className="px-3 py-2 text-white font-medium max-w-[180px] truncate">{row.name}</td>
                    <td className="px-3 py-2 text-slate-400">{row.salesman}</td>
                    <td className="px-3 py-2 text-right font-medium text-white">{fmtCur(row.balance)}</td>
                    <td className="px-3 py-2 text-right text-emerald-400">{row.current > 0 ? fmtCur(row.current) : ''}</td>
                    <td className="px-3 py-2 text-right text-blue-400">{row.over30 > 0 ? fmtCur(row.over30) : ''}</td>
                    <td className="px-3 py-2 text-right text-amber-400">{row.over60 > 0 ? fmtCur(row.over60) : ''}</td>
                    <td className="px-3 py-2 text-right text-red-400">{(row.over90 + row.over120) > 0 ? fmtCur(row.over90 + row.over120) : ''}</td>
                    <td className="px-3 py-2 text-right text-slate-400">{row.avgAging > 0 ? `${row.avgAging}d` : ''}</td>
                    <td className="px-3 py-2"><FlagBadges row={row} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredCustomers.length === 0 && !isLoading && (
            <div className="text-center py-8 text-slate-500 text-sm">No customers match the selected filters</div>
          )}
          <div className="px-3 py-2 border-t border-slate-700/30 text-xs text-slate-500">{filteredCustomers.length} customers</div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// Main Analytics Page
// ══════════════════════════════════════════════════════════════

export default function AnalyticsPage({ onBack }: AnalyticsPageProps) {
  const { branding } = useAuthContext();
  const [activeTab, setActiveTab] = useState<'sales' | 'ar'>('sales');

  const { data: filters } = useQuery({
    queryKey: ['analyticsFilters'],
    queryFn: () => api.getAnalyticsFilters(),
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <header className="border-b border-slate-800/50 backdrop-blur-sm bg-slate-950/50 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {onBack && (
                  <button onClick={onBack} className="p-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:text-white transition-all">
                    <ArrowLeft size={18} />
                  </button>
                )}
                <div>
                  <h1 className="text-lg font-semibold text-white">{branding?.company_name || 'Delldata Reports'} — Analytics</h1>
                </div>
              </div>

              {/* Tab toggle */}
              <div className="flex rounded-lg border border-slate-700/50 overflow-hidden">
                <button onClick={() => setActiveTab('sales')}
                  className={`px-4 py-2 text-sm font-medium flex items-center gap-2 transition-all ${activeTab === 'sales' ? 'bg-blue-600 text-white' : 'bg-slate-800/50 text-slate-400 hover:text-white'}`}>
                  <BarChart3 size={15} /> Sales Detail
                </button>
                <button onClick={() => setActiveTab('ar')}
                  className={`px-4 py-2 text-sm font-medium flex items-center gap-2 transition-all ${activeTab === 'ar' ? 'bg-blue-600 text-white' : 'bg-slate-800/50 text-slate-400 hover:text-white'}`}>
                  <CreditCard size={15} /> AR Detail
                </button>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {activeTab === 'sales' ? <SalesDetailTab filters={filters} /> : <ARDetailTab filters={filters} />}
        </main>
      </div>
    </div>
  );
}